import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { AgGridReact, AgGridProvider } from "ag-grid-react";
import {
  ClientSideRowModelModule,
  SelectEditorModule,
  ValidationModule,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import deleteIcon from "../../../assets/images/SBA/Delete.png";
import {
  getUpcomingEmails,
  updateUpcomingEmailStatus,
  deleteUpcomingEmail,
  getDraftEmail,
  previewDraftEmail,
  getEmailInfo,
} from "../services/adminEmail_service";
import useDocumentTitle from "../../../shared/customHooks/useDocumentTitle";
import SingleSelectDropdown from "../../../shared/components/SingleSelectDropdown";
import useAuthStore from "../../../store/authStore";
import { AESClientWebsiteDrawer } from "../../../shared/utils/aesClientWebsiteDrawer";
import ConfirmDialog from "../../../shared/components/ConfirmDialog";

const modules = [
  ClientSideRowModelModule,
  SelectEditorModule
];

const formatDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const mm = `${d.getMonth() + 1}`.padStart(2, "0");
  const dd = `${d.getDate()}`.padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}-${dd}-${yyyy}`;
};

const getStatusName = (webMailStatus, status) => {
  if (webMailStatus === 5 || webMailStatus === "Pending") return "Pending";
  if (status === 5) return "Pending";
  return "Scheduled";
};

export default function EmailUpcoming({ isActive = true }) {
  useDocumentTitle("Email Upcoming");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pageSize, setPageSize] = useState(15);

  const gridWrapperRef = useRef(null);
  const userId = useAuthStore((s) => s.user);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRecipients, setPreviewRecipients] = useState([]);
  const [previewDocuments, setPreviewDocuments] = useState([]);
  const [previewBody, setPreviewBody] = useState("");
  const [previewIndex, setPreviewIndex] = useState(0);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteRow, setPendingDeleteRow] = useState(null);

  const loadGrid = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getUpcomingEmails();
      const data = response?.data ?? response;
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setRows([]);
      setError("Failed to load upcoming emails.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isActive) {
      loadGrid();
    }
  }, [isActive, loadGrid]);

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .aes-drawer-header {
        background: #1e1a66 !important;
        color: #fff !important;
      }

      .aes-drawer-header .rs-drawer-title {
        font-weight: 600 !important;
        font-size: 20px !important;
        color: #fff !important;
        margin: 0 !important;
      }

      .aes-drawer-header .rs-drawer-header-close {
        color: #fff !important;
        opacity: 1 !important;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const updateStatus = useCallback(
    async (row) => {
      try {
        await updateUpcomingEmailStatus(row.emailId, 2, userId, row.isAttachment || false);
        await loadGrid();
      } catch (e) {
        setError("Failed to update status.");
      }
    },
    [loadGrid, userId]
  );

  const deleteEmail = useCallback((row) => {
    setPendingDeleteRow(row);
    setConfirmOpen(true);
  }, []);

  const confirmDelete = async () => {
    if (!pendingDeleteRow) return;

    try {
      await deleteUpcomingEmail(pendingDeleteRow.emailId);
      await loadGrid();
    } catch (e) {
      setError("Failed to delete email.");
    } finally {
      setConfirmOpen(false);
      setPendingDeleteRow(null);
    }
  };

  const cancelDelete = () => {
    setConfirmOpen(false);
    setPendingDeleteRow(null);
  };

  const openPreview = async (row) => {
    try {
      const emailId = row.emailId;

      const draftRes = await getDraftEmail(emailId);
      const draft = draftRes?.data?.data ?? draftRes?.data ?? draftRes;

      const templateBody = draft?.Body || draft?.body || "";
      if (!templateBody) {
        setError("Template body is empty.");
        return;
      }

      const previewRes = await previewDraftEmail(emailId, templateBody);
      const previewList = previewRes?.data?.data ?? previewRes?.data ?? previewRes;

      const infoRes = await getEmailInfo(emailId);
      const info = infoRes?.data?.data ?? infoRes?.data ?? infoRes;

      const recipients = Array.isArray(previewList) ? previewList : [];
      const normalizedRecipients = recipients.map((r) => ({
        sendName: r.sendName,
        firstName: r.firstName,
        lastName: r.lastName,
        webUserId: r.webUserId,
        mergedEmailBody: r.mergedEmailBody,
      }));

      const docList = info?.documentList ?? info?.DocumentList ?? [];
      const documents = Array.isArray(docList)
        ? docList.map((d) => ({
            documentName: d.documentName ?? d.DocumentName ?? "",
            documentDate: d.documentDate ?? d.DocumentDate ?? "",
          }))
        : [];

      setPreviewRecipients(normalizedRecipients);
      setPreviewDocuments(documents);
      setPreviewBody(normalizedRecipients[0]?.mergedEmailBody || "");
      setPreviewIndex(0);
      setPreviewOpen(true);
    } catch (e) {
      setError("Failed to load preview.");
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewRecipients([]);
    setPreviewDocuments([]);
    setPreviewBody("");
    setPreviewIndex(0);
  };

  const nextPreview = () => {
    if (previewIndex + 1 >= previewRecipients.length) return;
    const idx = previewIndex + 1;
    setPreviewIndex(idx);
    setPreviewBody(previewRecipients[idx]?.mergedEmailBody || "");
  };

  const prevPreview = () => {
    if (previewIndex <= 0) return;
    const idx = previewIndex - 1;
    setPreviewIndex(idx);
    setPreviewBody(previewRecipients[idx]?.mergedEmailBody || "");
  };

  const currentRecipient = previewRecipients[previewIndex] || {};

  const columnDefs = useMemo(
    () => [
      { field: "emailId", hide: true },
      { field: "templateId", hide: true },
      { field: "isAppointment", hide: true },
      { field: "modifiedBy", hide: true },
      { field: "status", hide: true },
      { field: "isAttachment", hide: true },

      { field: "subject", headerName: "Subject", minWidth: 280, flex: 1.2, sortable: true, filter: true },
      {
        field: "sendDate",
        headerName: "Date",
        minWidth: 190,
        sortable: true,
        filter: true,
        valueFormatter: (p) => formatDateTime(p.value),
      },
      {
        headerName: "Status",
        field: "status",
        cellRenderer: (p) => {
          const row = p.data;
          const value = getStatusName(row?.webMailStatus, row?.status);

          if (value === "Scheduled") {
            return "Scheduled";
          }

          return (
            <select
              style={{ color: "black" }}
              value="Pending"
              onChange={(e) => {
                if (e.target.value === "Schedule") {
                  const ok = window.confirm("Do you want to update status as Scheduled?");
                  if (ok) {
                    updateStatus(row);
                  } else {
                    e.target.value = "Pending";
                  }
                }
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <option style={{ color: "black" }} value="Pending">Pending</option>
              <option style={{ color: "black" }} value="Schedule">Schedule</option>
            </select>
          );
        }
      },
      { field: "createdName", headerName: "Created By", minWidth: 160, sortable: true, filter: true },
      { field: "recipientsCount", headerName: "Recipients", minWidth: 130, sortable: true, filter: true },
      { field: "documentCount", headerName: "Documents", minWidth: 130, sortable: true, filter: true },

      {
        headerName: "",
        minWidth: 30,
        sortable: false,
        filter: false,
        cellRenderer: (p) => (
          <button
            type="button"
            title="Preview"
            onClick={() => openPreview(p.data)}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: 0,
              marginTop: 2,
            }}
          >
            <i className="fa fa-search" aria-hidden="true"></i>
          </button>
        ),
      },
      {
        headerName: "",
        minWidth: 30,
        sortable: false,
        filter: false,
        cellRenderer: (p) => (
          <img
            src={deleteIcon}
            alt="Delete"
            title="Delete Upcoming"
            style={{ cursor: "pointer", width: 16, height: 16, marginTop: 2 }}
            onClick={() => deleteEmail(p.data)}
          />
        ),
      },
    ],
    [updateStatus, deleteEmail]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      floatingFilter: true,
    }),
    []
  );

  return (
    <div>
      {error ? <div style={{ color: "red", marginBottom: "12px" }}>{error}</div> : null}

      <AgGridProvider modules={modules}>
        <div ref={gridWrapperRef} className="ag-theme-alpine" style={{ width: "100%", height: "520px" }}>
          <AgGridReact
            theme={"legacy"}
            rowData={rows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={pageSize}
            paginationPageSizeSelector={[10, 15, 50, 100, 200]}
            suppressCellFocus={true}
            overlayNoRowsTemplate={"No Records Available"}
            getRowId={(p) => String(p.data?.emailId ?? p.data?.id)}
          />
        </div>
      </AgGridProvider>

      <AESClientWebsiteDrawer
        open={previewOpen}
        setOpen={setPreviewOpen}
        title="Mail Content Preview"
        size="md"
        HeaderClassName="aes-drawer-header"
      >
        <div style={{ padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <div>
              <strong>{currentRecipient.firstName} {currentRecipient.lastName}</strong>, {currentRecipient.webUserId}
            </div>

            <div>
              {previewRecipients.length ? previewIndex + 1 : 0} of {previewRecipients.length} Recipients
              <span style={{ marginLeft: 10 }}>
                <a href="#" onClick={(e) => { e.preventDefault(); prevPreview(); }}>&#8249;</a>
                <a href="#" onClick={(e) => { e.preventDefault(); nextPreview(); }} style={{ marginLeft: 8 }}>&#8250;</a>
              </span>
            </div>
          </div>

          <hr style={{ borderTop: "1px solid #2C81BA" }} />

          <div dangerouslySetInnerHTML={{ __html: previewBody }} />

          <div style={{ marginTop: 16, display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <table className="table table-bordered table-striped">
                <thead>
                  <tr>
                    <th>Recipient Name</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRecipients.map((r, idx) => (
                    <tr key={idx}>
                      <td>{r.sendName || `${r.firstName || ""} ${r.lastName || ""}`}</td>
                      <td>{r.webUserId}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ flex: 1 }}>
              <table className="table table-bordered table-striped">
                <thead>
                  <tr>
                    <th>Document Name</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {previewDocuments.map((d, idx) => (
                    <tr key={idx}>
                      <td>{d.documentName}</td>
                      <td>{formatDate(d.documentDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
            <button className="btn btn-default" onClick={closePreview}>
              Cancel
            </button>
          </div>
        </div>
      </AESClientWebsiteDrawer>

      <ConfirmDialog
        open={confirmOpen}
        title="Do you want to Delete Email?"
        message="This Email will be deleted permanently. Are you sure?"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
}
